import { io, Socket } from 'socket.io-client';
import {
  QUESTIONNAIRE_DRAFT_SOCKET,
  ReportStatusEvent,
  USER_IMPORT_SESSION_LATEST_CHANGED_EVENT,
} from '@digital-twins/shared-types';
import type {
  ImportSessionUpdatedEvent,
  KnowledgeDocumentIndexingUpdatedEvent,
  KnowledgeLinkIngestUpdatedEvent,
  QuestionnaireChatTurnStageEvent,
  QuestionnaireChatTurnStatusEvent,
  ReportAnalystRunUpdatedPayload,
  UserImportSessionLatestChangedEvent,
} from '@digital-twins/shared-types';
export type {
  ImportSessionUpdatedEvent,
  KnowledgeDocumentIndexingUpdatedEvent,
  KnowledgeLinkIngestUpdatedEvent,
} from '@digital-twins/shared-types';

let socket: Socket | null = null;
const importSessionSubscriptionCounts = new Map<string, number>();
const activeImportSessionSubscriptions = new Set<string>();
// Desired experiment rooms. Unlike a "currently joined" set, this survives
// disconnect so every connect replays subscribe: the one owner of re-joining
// a room after a reconnect.
const desiredExperimentSubscriptions = new Set<string>();
// Quick Study durable runs (Step 9): the DESIRED subscription set. Unlike the two sets
// above it deliberately survives a disconnect — a mid-run socket reconnect must re-join
// the run room without the page doing anything (see the 'connect' handler below).
const desiredQuickStudyRunSubscriptions = new Set<string>();
const desiredQuestionnaireDraftSubscriptions = new Set<string>();

/**
 * Always connect through the **same origin** (page host) so the browser sends
 * the httpOnly `fairgen-jwt` cookie on the WebSocket upgrade request.
 *
 * Path `/api/socket.io/` is proxied to the backend in every environment:
 *   - Local dev: Vite proxy (`ws: true`)
 *   - Docker / prod: nginx `location /api/`
 *
 * This avoids the cross-origin cookie problem that occurred when the client
 * connected directly to `ws://localhost:4000` (different port → browser
 * omits same-site cookies on the WS upgrade).
 */
export const connectWebSocket = (): Socket => {
  if (!socket) {
    const baseUrl =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : '';

    const ws = io(baseUrl, {
      transports: ['websocket', 'polling'],
      path: '/api/socket.io/',
      autoConnect: true,
      withCredentials: true,
    });
    socket = ws;

    ws.on('disconnect', (reason) => {
      console.warn('[WebSocket] disconnected:', reason);
      activeImportSessionSubscriptions.clear();
    });

    ws.on('connect', () => {
      console.log('[WebSocket] connected, id:', ws.id);
      for (const experimentId of desiredExperimentSubscriptions) {
        ws.emit('subscribe', { experimentId });
      }
      // Quick Study durable runs: re-join every desired run room after a reconnect so a
      // mid-run network blip resumes frames mechanically (Step 9 (F) socket-reconnect).
      for (const runId of desiredQuickStudyRunSubscriptions) {
        ws.emit('subscribe-quick-study-run', { runId });
      }
      for (const draftId of desiredQuestionnaireDraftSubscriptions) {
        ws.emit(QUESTIONNAIRE_DRAFT_SOCKET.subscribe, { draftId });
      }
      for (const threadToken of desiredKnowledgeThreadSubscriptions.keys()) {
        ws.emit('subscribe-knowledge-thread', { threadToken });
      }
    });

    ws.on('connect_error', (error) => {
      console.error('[WebSocket] connect_error:', error.message);
    });

    ws.on('error', (data: unknown) => {
      console.error('[WebSocket] server error event:', data);
    });
  } else {
    if (!socket.connected) {
      socket.connect();
    }
  }
  return socket;
};

export const disconnectWebSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  activeImportSessionSubscriptions.clear();
  desiredExperimentSubscriptions.clear();
  desiredQuickStudyRunSubscriptions.clear();
  desiredQuestionnaireDraftSubscriptions.clear();
  desiredKnowledgeThreadSubscriptions.clear();
};

export const subscribeToExperiment = (experimentId: string) => {
  desiredExperimentSubscriptions.add(experimentId);
  const ws = connectWebSocket();
  ws.emit('subscribe', { experimentId });
};

export const unsubscribeFromExperiment = (experimentId: string) => {
  desiredExperimentSubscriptions.delete(experimentId);
  if (socket?.connected) {
    socket.emit('unsubscribe', { experimentId });
  }
};

/**
 * Subscribe to import-session events. Emits immediately when transitioning from
 * 0 to 1 subscribers; Socket.IO queues the emit when disconnected so no events
 * are lost while waiting for connection.
 */
export const subscribeToImportSession = (importSessionId: string) => {
  const currentCount = importSessionSubscriptionCounts.get(importSessionId) ?? 0;
  importSessionSubscriptionCounts.set(importSessionId, currentCount + 1);

  if (currentCount === 0) {
    const ws = connectWebSocket();
    ws.emit('subscribe-import-session', { importSessionId });
    activeImportSessionSubscriptions.add(importSessionId);
  }
};

export const resubscribeToImportSession = (importSessionId: string) => {
  if ((importSessionSubscriptionCounts.get(importSessionId) ?? 0) === 0) {
    return;
  }
  if (activeImportSessionSubscriptions.has(importSessionId)) {
    return;
  }
  const ws = connectWebSocket();
  ws.emit('subscribe-import-session', { importSessionId });
  activeImportSessionSubscriptions.add(importSessionId);
};

export const unsubscribeFromImportSession = (importSessionId: string) => {
  const currentCount = importSessionSubscriptionCounts.get(importSessionId) ?? 0;
  if (currentCount <= 1) {
    importSessionSubscriptionCounts.delete(importSessionId);
    if (socket?.connected && activeImportSessionSubscriptions.has(importSessionId)) {
      const ws = connectWebSocket();
      ws.emit('unsubscribe-import-session', { importSessionId });
    }
    activeImportSessionSubscriptions.delete(importSessionId);
    return;
  }

  importSessionSubscriptionCounts.set(importSessionId, currentCount - 1);
};

// ── Quick Study durable runs (Step 9) ────────────────────────────────────────

/** Join a Quick Study run room; the subscription survives reconnects until unsubscribed. */
export const subscribeToQuickStudyRun = (runId: string) => {
  desiredQuickStudyRunSubscriptions.add(runId);
  const ws = connectWebSocket();
  ws.emit('subscribe-quick-study-run', { runId });
};

/** Re-emit the join for an already-desired run. */
export const resubscribeToQuickStudyRun = (runId: string) => {
  if (!desiredQuickStudyRunSubscriptions.has(runId)) {
    return;
  }
  const ws = connectWebSocket();
  ws.emit('subscribe-quick-study-run', { runId });
};

export const unsubscribeFromQuickStudyRun = (runId: string) => {
  desiredQuickStudyRunSubscriptions.delete(runId);
  if (socket?.connected) {
    socket.emit('unsubscribe-quick-study-run', { runId });
  }
};

/**
 * Listen for a run's frame events. The payload is the parsed SSE frame VERBATIM
 * (`{type:'progress',completed,total,answer?}` / `{type:'analyzing'}` / `{type:'result',data}`
 * / `{type:'skipped',reason}` / `{type:'error',message}`) — feed it into the same evt-switch
 * the SSE parser feeds.
 */
export const onQuickStudyRunFrame = (
  runId: string,
  callback: (frame: Record<string, unknown>) => void,
) => {
  const ws = connectWebSocket();
  const event = `quick-study-run:${runId}:frame`;
  ws.on(event, callback);
  return () => {
    ws.off(event, callback);
  };
};

/** Listen for a run's lifecycle status events; the terminal event carries `charge`. */
export const onQuickStudyRunStatus = (
  runId: string,
  callback: (payload: {
    status: string;
    errorMessage?: string;
    charge?: {
      charged: boolean;
      creditCost?: number;
      chargeType?: string;
      executedReads?: number;
      registeredReads?: number;
      /**
       * Result-lost auto-refund: true when a result-lost run's charge was refunded. `charged`
       * STAYS true (the historical fact); `refunded` is the later reversal — the FE nets the run
       * to zero local spend and shows the "credits returned" result-lost copy.
       */
      refunded?: boolean;
      /** The exact credits returned by the refund — present only alongside `refunded === true`. */
      refundedCredits?: number;
    };
    /**
     * Set on a `running` status event when the worker's python stream dropped mid-run (a
     * transport failure, NOT a run failure — the durable run keeps executing server-side). The
     * gateway spreads the backend `detail` onto the payload, so it lands top-level here. A live
     * FE shows an honest "finishing server-side / reconnecting" sublabel instead of a false fail.
     */
    disconnected?: boolean;
    timestamp?: string;
  }) => void,
) => {
  const ws = connectWebSocket();
  const event = `quick-study-run:${runId}:status`;
  ws.on(event, callback);
  return () => {
    ws.off(event, callback);
  };
};

export const subscribeToQuestionnaireDraft = (draftId: string) => {
  desiredQuestionnaireDraftSubscriptions.add(draftId);
  const ws = connectWebSocket();
  ws.emit(QUESTIONNAIRE_DRAFT_SOCKET.subscribe, { draftId });
};

export const onQuestionnaireChatTurnStage = (
  draftId: string,
  callback: (payload: QuestionnaireChatTurnStageEvent) => void,
) => {
  const ws = connectWebSocket();
  const event = QUESTIONNAIRE_DRAFT_SOCKET.turnStage(draftId);
  ws.on(event, callback);
  return () => {
    ws.off(event, callback);
  };
};

export const onQuestionnaireChatTurnStatus = (
  draftId: string,
  callback: (payload: QuestionnaireChatTurnStatusEvent) => void,
) => {
  const ws = connectWebSocket();
  const event = QUESTIONNAIRE_DRAFT_SOCKET.turnStatus(draftId);
  ws.on(event, callback);
  return () => {
    ws.off(event, callback);
  };
};

export const onExperimentProgress = (
  experimentId: string,
  callback: (progress: any) => void,
) => {
  const ws = connectWebSocket();
  const event = `experiment:${experimentId}:progress`;
  ws.on(event, callback);
  return () => {
    ws.off(event, callback);
  };
};

export const onExperimentStatus = (
  experimentId: string,
  callback: (status: any) => void,
) => {
  const ws = connectWebSocket();
  const event = `experiment:${experimentId}:status`;
  ws.on(event, callback);
  return () => {
    ws.off(event, callback);
  };
};

/** Refetch ping for a report-analyst run. Delivered on the owner's user room. */
export const onReportAnalystRunUpdated = (
  experimentId: string,
  callback: (data: ReportAnalystRunUpdatedPayload) => void,
) => {
  const ws = connectWebSocket();
  const event = `experiment:${experimentId}:report-analyst`;
  ws.on(event, callback);
  return () => {
    ws.off(event, callback);
  };
};

export const onImportSessionUpdated = (
  importSessionId: string,
  callback: (data: ImportSessionUpdatedEvent) => void,
) => {
  const ws = connectWebSocket();
  const event = `import-session:${importSessionId}:updated`;
  ws.on(event, callback);
  return () => {
    ws.off(event, callback);
  };
};

export const onExperimentResultsWave = (
  experimentId: string,
  callback: (data: { responses: Array<Record<string, unknown>> }) => void,
) => {
  const ws = connectWebSocket();
  const event = `experiment:${experimentId}:results_wave`;
  ws.on(event, callback);
  return () => {
    ws.off(event, callback);
  };
};

/** Generated in `@digital-twins/shared-types` (`ReportStatusEvent`). */
export type DeckStatusEvent = ReportStatusEvent;

// Keyed by the shared union, so a new value fails to compile until handled here.
const REPORT_STATUS_EVENTS: Record<ReportStatusEvent, true> = {
  deck_processing: true,
  deck_failed: true,
  report_failed: true,
  report_ready: true,
};

export function isReportStatusEvent(status: unknown): status is ReportStatusEvent {
  return typeof status === 'string' && Object.prototype.hasOwnProperty.call(REPORT_STATUS_EVENTS, status);
}

export const onDeckStatus = (
  experimentId: string,
  callback: (status: DeckStatusEvent) => void,
) => {
  const ws = connectWebSocket();
  const event = `experiment:${experimentId}:status`;
  const handler = (data: { status: string }) => {
    if (isReportStatusEvent(data.status)) {
      callback(data.status);
    }
  };
  ws.on(event, handler);
  return () => {
    ws.off(event, handler);
  };
};

// Study-list notifications (delivered to the study's owner and organization rooms)
export const onExperimentCompleted = (
  callback: (data: { experimentId: string; experimentName: string; timestamp: string }) => void,
) => {
  const ws = connectWebSocket();
  ws.on('notification:experiment_completed', callback);
  return () => {
    ws.off('notification:experiment_completed', callback);
  };
};

export const onPopulationUpdated = (
  callback: (data: {
    populationId: string;
    status?: string;
    processingError?: string | null;
    previousStatus?: string;
    reprocessStatus?: 'completed' | 'failed';
    relaunchFailed?: boolean;
    timestamp: string;
  }) => void,
) => {
  const ws = connectWebSocket();
  ws.on('population:updated', callback);
  return () => {
    ws.off('population:updated', callback);
  };
};

export const onKnowledgeDocumentIndexingUpdated = (
  callback: (data: KnowledgeDocumentIndexingUpdatedEvent) => void,
) => {
  const ws = connectWebSocket();
  ws.on('knowledge:document-indexing-updated', callback);
  return () => {
    ws.off('knowledge:document-indexing-updated', callback);
  };
};

export const onKnowledgeLinkIngestUpdated = (
  callback: (data: KnowledgeLinkIngestUpdatedEvent) => void,
) => {
  const ws = connectWebSocket();
  ws.on('knowledge:link-ingest-updated', callback);
  return () => {
    ws.off('knowledge:link-ingest-updated', callback);
  };
};

export const onExperimentStatusChanged = (
  callback: (data: { experimentId: string; status: string; timestamp: string }) => void,
) => {
  const ws = connectWebSocket();
  ws.on('notification:experiment_status_changed', callback);
  return () => {
    ws.off('notification:experiment_status_changed', callback);
  };
};

export const onExperimentReadinessChanged = (
  callback: (data: {
    experimentId: string;
    reportStatus: string | null;
    deckStatus: string | null;
    deckJsonKey: string | null;
    timestamp: string;
  }) => void,
) => {
  const ws = connectWebSocket();
  ws.on('notification:experiment_readiness_changed', callback);
  return () => {
    ws.off('notification:experiment_readiness_changed', callback);
  };
};

export const onExperimentReadiness = (
  experimentId: string,
  callback: (data: {
    reportStatus: string | null;
    deckStatus: string | null;
    deckJsonKey: string | null;
    timestamp: string;
  }) => void,
) => {
  const ws = connectWebSocket();
  const event = `experiment:${experimentId}:readiness`;
  ws.on(event, callback);
  return () => {
    ws.off(event, callback);
  };
};

export const onDraftStatusChanged = (
  callback: (data: { draftId: string; status: string; timestamp: string }) => void,
) => {
  const ws = connectWebSocket();
  ws.on('notification:draft_status_changed', callback);
  return () => {
    ws.off('notification:draft_status_changed', callback);
  };
};

export const onFeaturedTwinsEnriched = (
  callback: (data: { twins: Array<Record<string, unknown>>; timestamp: string }) => void,
) => {
  const ws = connectWebSocket();
  ws.on('featured-twins:enriched', callback);
  return () => {
    ws.off('featured-twins:enriched', callback);
  };
};

export const onUserImportSessionLatestChanged = (
  callback: (data: UserImportSessionLatestChangedEvent) => void,
) => {
  const ws = connectWebSocket();
  ws.on(USER_IMPORT_SESSION_LATEST_CHANGED_EVENT, callback);
  return () => {
    ws.off(USER_IMPORT_SESSION_LATEST_CHANGED_EVENT, callback);
  };
};

// Counted per thread: the run pane and the canvas pictures each hold the room, and
// leaving it is sent only when the last holder lets go.
const desiredKnowledgeThreadSubscriptions = new Map<string, number>();

export function subscribeToKnowledgeThread(threadToken: string): void {
  desiredKnowledgeThreadSubscriptions.set(threadToken, (desiredKnowledgeThreadSubscriptions.get(threadToken) ?? 0) + 1);
  const ws = connectWebSocket();
  ws.emit('subscribe-knowledge-thread', { threadToken });
}

export function unsubscribeFromKnowledgeThread(threadToken: string): void {
  const holders = (desiredKnowledgeThreadSubscriptions.get(threadToken) ?? 0) - 1;
  if (holders > 0) {
    desiredKnowledgeThreadSubscriptions.set(threadToken, holders);
    return;
  }
  desiredKnowledgeThreadSubscriptions.delete(threadToken);
  const ws = connectWebSocket();
  if (ws.connected) {
    ws.emit('unsubscribe-knowledge-thread', { threadToken });
  }
}

export function onKnowledgeThreadCanvasImages(
  threadToken: string,
  callback: (data: { threadToken: string; messageToken: string; timestamp: string }) => void,
): () => void {
  const ws = connectWebSocket();
  const event = `knowledge-thread:${threadToken}:canvas-images`;
  ws.on(event, callback);
  return () => {
    ws.off(event, callback);
  };
}

export function onKnowledgeThreadMessageAdded(
  threadToken: string,
  callback: (data: {
    threadToken: string;
    askId: string | null;
    timestamp: string;
  }) => void,
): () => void {
  const ws = connectWebSocket();
  const event = `knowledge-thread:${threadToken}:message-added`;
  ws.on(event, callback);
  return () => {
    ws.off(event, callback);
  };
}

export const getSocket = () => socket;
